import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCategories } from './catalog.api';

/**
 * The whole menu at a glance: every department with the categories under it.
 *
 * Built by grouping the category list rather than asking for a second one —
 * every category already carries its department's name, which is exactly what
 * the original did, except it grouped on a string of three fields joined by a
 * separator and then split them back apart.
 */
export function BreakdownSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';
  const categories = useCategories();

  const groups = useMemo(() => {
    const byDepartment = new Map<
      string,
      {
        name: string;
        items: { id: string; name: string; imageUrl: string | null }[];
      }
    >();

    for (const category of categories.data ?? []) {
      const key = category.superSectionId ?? 'none';
      const departmentName =
        (isEnglish
          ? category.superSectionNameEng
          : category.superSectionName) ?? t('catalog.noDepartment');

      if (!byDepartment.has(key)) {
        byDepartment.set(key, { name: departmentName, items: [] });
      }
      byDepartment.get(key)!.items.push({
        id: category.id,
        name: (isEnglish ? category.nameEng : category.nameSpa) ?? '—',
        imageUrl: category.imageUrl,
      });
    }

    return [...byDepartment.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [categories.data, isEnglish, t]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('catalog.viewBreakdown')}</SheetTitle>
        </SheetHeader>

        <div className="p-4">
          {categories.isPending && (
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-card rounded-xl border">
                  <div className="border-b p-3">
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <div className="space-y-1.5 p-3">
                    {Array.from({ length: 4 }).map((_, row) => (
                      <div key={row} className="flex items-center gap-2">
                        <Skeleton className="size-6 shrink-0 rounded" />
                        <Skeleton className="h-4 w-36" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {categories.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('catalog.loadFailed')}
            </p>
          )}

          {categories.isSuccess && groups.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={categories.isFetching} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                {t('catalog.noCategories')}
              </p>
            </div>
          )}

          {groups.length > 0 && (
            <div className="relative">
              <BusyOverlay show={categories.isFetching} />
              <div
                className={cn(
                  'grid gap-3 sm:grid-cols-2',
                  categories.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                {groups.map((group) => (
                  <div key={group.name} className="bg-card rounded-xl border">
                    <p className="border-b p-3 text-sm font-semibold">
                      {group.name}
                      <span className="text-muted-foreground ml-1.5 text-xs font-normal tabular-nums">
                        {group.items.length}
                      </span>
                    </p>
                    <div className="space-y-1.5 p-3">
                      {group.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-2">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt=""
                              className="size-6 shrink-0 object-contain"
                            />
                          ) : (
                            <span className="bg-muted size-6 shrink-0 rounded" />
                          )}
                          <span className="min-w-0 truncate text-sm">
                            {item.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
